import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './client.js';
import type { Tenant, Extension, Cdr, CdrStats, Pagination, PhonebookMatch } from '@arcnet/contracts';

interface ListResponse<T> { data: T[]; pagination: Pagination }

/**
 * Query keys are structured so invalidation can be precise (doc 07 section 3).
 * `['extensions', tenantId, filters]` lets a mutation invalidate one tenant's
 * extension list without touching every other cached list in the app.
 */
export const keys = {
  tenants: (params?: unknown) => ['tenants', params] as const,
  tenant: (id: string) => ['tenant', id] as const,
  extensions: (tenantId: string, params?: unknown) => ['extensions', tenantId, params] as const,
  extension: (tenantId: string, id: string) => ['extension', tenantId, id] as const,
  cdrs: (tenantId: string, params?: unknown) => ['cdrs', tenantId, params] as const,
  cdrStats: (tenantId: string) => ['cdr-stats', tenantId] as const,
};

const qs = (params: Record<string, string | number | boolean | undefined>): string => {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
};

export function useTenants(params: { q?: string | undefined; status?: string | undefined; limit?: number | undefined } = {}) {
  return useQuery({
    queryKey: keys.tenants(params),
    queryFn: () => api.get<ListResponse<Tenant>>(`/v1/tenants${qs(params)}`),
  });
}

export function useTenant(id: string | null) {
  return useQuery({
    queryKey: keys.tenant(id ?? ''),
    queryFn: () => api.get<Tenant>(`/v1/tenants/${id}`),
    enabled: !!id,
  });
}

export function useExtensions(
  tenantId: string | null,
  params: { q?: string | undefined; status?: string | undefined; limit?: number | undefined; cursor?: string | undefined } = {},
) {
  return useQuery({
    queryKey: keys.extensions(tenantId ?? '', params),
    queryFn: () => api.get<ListResponse<Extension>>(`/v1/tenants/${tenantId}/extensions${qs(params)}`),
    enabled: !!tenantId,
  });
}

/**
 * The response carries the SIP password, ONCE.
 *
 * It is minted server-side when the seat is created and can only be shown again
 * under step-up, so a caller that discards this response has thrown away the
 * only easy copy — and the handset it was meant for cannot be configured.
 */
export interface CreatedExtension extends Extension {
  sip_credential?: { username: string; realm: string; password: string };
}

export function useCreateExtension(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<CreatedExtension>(`/v1/tenants/${tenantId}/extensions`, body),
    // Narrowest possible invalidation: this tenant's extension lists only.
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['extensions', tenantId] }); },
  });
}

export interface RangeResult {
  count: number;
  data: Array<{
    id: string; number: string; display_name: string;
    sip_credential: { username: string; realm: string; password: string } | null;
  }>;
}

/**
 * A block of seats in one request.
 *
 * The server refuses the whole range if any part of it is unusable, so there is
 * no partial-success path to handle here -- either every seat in `data` was
 * created, or nothing was and this rejected.
 */
export function useCreateExtensionRange(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<RangeResult>(`/v1/tenants/${tenantId}/extensions/range`, body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['extensions', tenantId] }); },
  });
}

export function useUpdateExtension(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      api.patch<Extension>(`/v1/tenants/${tenantId}/extensions/${id}`, body),
    /**
     * The open extension is read back as well as the list, and the save stays pending until it
     * is. Refreshing the list alone left Details showing the extension as first loaded, so a
     * seat type that had saved looked as if it had not (15 September 2026).
     */
    onSuccess: (_saved, { id }) => Promise.all([
      qc.invalidateQueries({ queryKey: ['extensions', tenantId] }),
      qc.invalidateQueries({ queryKey: ['extension', tenantId, id] }),
    ]),
  });
}

export function useDeleteExtension(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/v1/tenants/${tenantId}/extensions/${id}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['extensions', tenantId] }); },
  });
}

/** What call history can be narrowed by (CdrListQuerySchema). */
export interface CdrFilter {
  q?: string | undefined;
  direction?: string | undefined;
  answered?: boolean | undefined;
  recorded?: boolean | undefined;
  extension_id?: string | undefined;
  /** ISO timestamps: from, inclusive, to, exclusive. */
  from?: string | undefined;
  to?: string | undefined;
}

/** Call history a page at a time, newest first; the next page follows the cursor. */
export function useCdrs(tenantId: string | null, f: CdrFilter = {}, limit = 50) {
  return useInfiniteQuery({
    queryKey: keys.cdrs(tenantId ?? '', { ...f, limit }),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => api.get<ListResponse<Cdr>>(`/v1/tenants/${tenantId}/cdrs${qs({
      limit, q: f.q || undefined, direction: f.direction || undefined,
      answered: f.answered, recorded: f.recorded, extension_id: f.extension_id || undefined,
      'started_at[gte]': f.from, 'started_at[lt]': f.to, cursor: pageParam ?? undefined,
    })}`),
    getNextPageParam: (last) => last.pagination?.next_cursor ?? null,
    enabled: !!tenantId,
  });
}

/**
 * One call, fetched when its row is opened and not before.
 *
 * The list deliberately does not carry this -- hangup causes, rate rules, the
 * other legs and the channel variables would multiply a hundred-row page by ten
 * for the one row somebody looks at.
 */
export interface CdrDetail extends Cdr {
  parent_id: string | null;
  extension_number: string | null;
  is_billable_leg: boolean;
  /** Left over the client's own trunk, so not charged. */
  on_own_trunk?: boolean;
  rtt_ms: string | null;
  flow_trace: unknown;
  /**
   * The recording's digests and format, when the call was recorded (0096, 0097), and how many
   * times it was paused for card details (0116).
   */
  recording?: {
    sha256: string | null; source_sha256: string | null; format: string; bytes: number | null;
    pauses: number;
  };
  /** Staff only: supplier terms and the raw channel. Absent for a tenant. */
  trunk_id?: string | null;
  trunk_name?: string | null;
  rate_deck_version_id?: string | null;
  rate_rule_id?: string | null;
  rated_on_node?: string | null;
  rating_mismatch?: boolean;
  variables?: Record<string, unknown> | null;
  legs: Array<{
    id: string; leg: string; direction: string;
    from_number: string | null; to_number: string | null;
    started_at: string; duration_sec: number;
    hangup_cause: string | null; sip_code: number | null;
    trunk_name?: string | null;
  }>;
}

export function useCdrDetail(tenantId: string | null, id: string | null) {
  return useQuery({
    queryKey: ['cdr', tenantId ?? '', id ?? ''] as const,
    queryFn: () => api.get<CdrDetail>(`/v1/tenants/${tenantId}/cdrs/${id}`),
    enabled: !!tenantId && !!id,
    // A finished call does not change. Refetching it on every focus would be
    // requests spent to receive the same answer.
    staleTime: 5 * 60_000,
  });
}

export function useCdrStats(tenantId: string | null) {
  return useQuery({
    queryKey: keys.cdrStats(tenantId ?? ''),
    queryFn: () => api.get<CdrStats>(`/v1/tenants/${tenantId}/cdrs/stats`),
    enabled: !!tenantId,
  });
}

/**
 * The account lifecycle: suspend, resume, close, archive.
 *
 * Each is its own act with its own audit row, rather than a status dropdown --
 * suspending a customer for non-payment and ending the relationship are not
 * the same decision, and a select box makes them look like it. Suspend and
 * close both demand a written reason, because "why is this customer off?" is
 * asked at the worst possible moment.
 */
export function useTenantLifecycle(tenantId: string) {
  const qc = useQueryClient();
  const done = () => {
    void qc.invalidateQueries({ queryKey: keys.tenant(tenantId) });
    void qc.invalidateQueries({ queryKey: ['tenants'] });
  };
  return {
    suspend: useMutation({
      mutationFn: (reason: string) =>
        api.post(`/v1/tenants/${tenantId}/actions/suspend`, { reason }),
      onSuccess: done,
    }),
    resume: useMutation({
      mutationFn: () => api.post(`/v1/tenants/${tenantId}/actions/resume`, {}),
      onSuccess: done,
    }),
    close: useMutation({
      mutationFn: (reason: string) =>
        api.post(`/v1/tenants/${tenantId}/actions/close`, { reason }),
      onSuccess: done,
    }),
    archive: useMutation({
      mutationFn: () => api.delete(`/v1/tenants/${tenantId}`),
      onSuccess: done,
    }),
  };
}

/**
 * Who these numbers belong to, from the account's phonebook (API phonebook/lookup).
 *
 * Asked for a whole screen at once -- the call ringing now and the calls behind it -- because one
 * request for a page beats twenty for its rows. Numbers are sorted into the key so the same set
 * asked in a different order is the same cached answer.
 */
export function usePhonebookLookup(tenantId: string | null, numbers: string[]) {
  const wanted = [...new Set(numbers.filter(Boolean))].sort();
  return useQuery({
    queryKey: ['phonebook-lookup', tenantId ?? '', wanted.join(',')],
    queryFn: () => api.get<{ data: Record<string, PhonebookMatch> }>(
      `/v1/tenants/${tenantId}/phonebook/lookup?numbers=${encodeURIComponent(wanted.join(','))}`),
    enabled: !!tenantId && wanted.length > 0,
    // A contact does not change while somebody is on a call with them.
    staleTime: 5 * 60_000,
  });
}
